/**
 * Top-level React error boundary.
 *
 * Catches rendering errors anywhere below it in the tree, logs them
 * (console for dev, can be wired to a remote sink later), and renders
 * a clean fallback card so an exception in a single page doesn't blank
 * the entire app.
 *
 * Error boundaries MUST be class components — there is no hook
 * equivalent for `componentDidCatch` / `getDerivedStateFromError`.
 *
 * Usage:
 *
 *   <ErrorBoundary>
 *     <RouterProvider router={router} />
 *   </ErrorBoundary>
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { reportRenderError } from '../features/errors/clientErrorReporting';

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Optional override for the fallback UI. Receives the caught error and
   * a `reset` callback that clears the error state so children re-render.
   * If omitted, the default branded fallback is used.
   */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log to the console with the React component stack so the failing
    // tree is visible during dev. The console is the developer's own machine,
    // so the full message and stack belong here.
    console.error('[ErrorBoundary] Render error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    // P1 capture. Deliberately takes NO argument: the platform records that a
    // render failed on this route and nothing about what the error said. The
    // message above stays on this machine. Never throws, so the fallback below
    // renders whether or not the report succeeds.
    reportRenderError();
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (!error) return children;

    if (fallback) return fallback(error, this.reset);

    // Default fallback UI — matches the app's existing card-based
    // Tailwind look (slate / red palette, lucide icons).
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-lg w-full bg-white rounded-lg shadow-lg border border-slate-200 p-8">
          <div className="flex items-start gap-4 mb-4">
            <div className="rounded-full bg-red-100 p-3 flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 mb-1">
                Something went wrong
              </h1>
              <p className="text-sm text-slate-600">
                The page hit an unexpected error. Try reloading; if the problem
                persists, your data is safe — please report it to your study
                administrator.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-6">
            <p className="text-xs font-mono text-slate-700 break-words">
              {error.message || String(error)}
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={this.reset}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-white text-slate-700 text-sm font-medium rounded border border-slate-300 hover:bg-slate-50 transition-colors"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
